import type {
  ClashChoice,
  ImportItem,
  ImportKind,
  ImportMode,
  ImportPageState,
  ImportPreview,
  ImportPreviewOrg,
} from "./bpaImport";
import { ALL_KINDS, firmKey, KIND_OF_ITEM, slotsInOrder } from "./bpaImport";
import { languageName } from "./languageName";
import { changeCount, firmControl, previewTotals } from "./bpaImportItemControl";

export type ReviewGroupId = "replaces" | "keeps" | "nothingExisting" | "leftOut";

export type ReviewFirm = {
  key: string;
  name: string;
  choice: ClashChoice | null;
  changes: number;
  line: string;
};

export type ReviewGroup = {
  id: ReviewGroupId;
  title: string;
  note: string | null;
  open: boolean;
  firms: ReviewFirm[];
};

export type ReviewFigure = { value: number; label: string; warn?: boolean };

export type ItemLine = {
  kindLabel: "Submission" | "Ranking" | "Commentary";
  sentence: string;
  itemIds: string[];
  removedWithNothing: boolean;
};

const GROUP_TITLES: Record<ReviewGroupId, string> = {
  replaces: "Replaces target content",
  keeps: "Keeps target content",
  nothingExisting: "Only adds new content",
  leftOut: "Left out",
};

const GROUP_ORDER: ReviewGroupId[] = ["replaces", "keeps", "nothingExisting", "leftOut"];

const KIND_LABEL_OF_ITEM: Record<ImportItem["kind"], ItemLine["kindLabel"]> = {
  submission: "Submission",
  firm_ranking: "Ranking",
  lawyer_ranking: "Ranking",
  commentary: "Commentary",
};

const KIND_NOUNS: Record<ImportKind, [string, string]> = {
  submissions: ["submission", "submissions"],
  rankings: ["ranking", "rankings"],
  commentary: ["commentary entry", "commentary entries"],
};

const VISIBLE_FIRM_LIMIT = 5;

function joinWithAnd(parts: string[]): string {
  if (parts.length <= 1) return parts.join("");
  return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

function kindPhrase(kind: ImportKind, count: number): string {
  const [one, many] = KIND_NOUNS[kind];
  return `${count} ${count === 1 ? one : many}`;
}

function kindPhrases(items: ImportItem[]): string {
  const kinds = ALL_KINDS;
  const phrases = kinds
    .map(kind => kindPhrase(kind, items.filter(item => KIND_OF_ITEM[item.kind] === kind).length))
    .filter((_, index) => items.some(item => KIND_OF_ITEM[item.kind] === kinds[index]));
  return joinWithAnd(phrases);
}

function sourceItems(org: ImportPreviewOrg): ImportItem[] {
  return org.items?.source ?? [];
}

function targetItems(org: ImportPreviewOrg): ImportItem[] {
  return org.items?.target ?? [];
}

function isTicked(org: ImportPreviewOrg, state: ImportPageState): boolean {
  return !state.unticked.includes(firmKey(org));
}

function deletesExistingItem(org: ImportPreviewOrg): boolean {
  return targetItems(org).some(item => item.outcome === "replaced" || item.outcome === "removed");
}

function groupOf(org: ImportPreviewOrg, state: ImportPageState): ReviewGroupId {
  if (!isTicked(org, state)) return "leftOut";
  if (!org.clash) return "nothingExisting";
  return deletesExistingItem(org) ? "replaces" : "keeps";
}

function outcomeCounts(org: ImportPreviewOrg) {
  const target = targetItems(org);
  const countTarget = (outcome: ImportItem["outcome"]) =>
    target.filter(item => item.outcome === outcome).length;
  return {
    replaced: countTarget("replaced"),
    removed: countTarget("removed"),
    kept: countTarget("kept"),
    added: sourceItems(org).filter(item => item.outcome === "new" && !item.sharedSlot).length,
  };
}

type OutcomeCounts = ReturnType<typeof outcomeCounts>;

function countsText(counts: OutcomeCounts): string {
  return [
    counts.replaced > 0 ? `${counts.replaced} replaced` : null,
    counts.removed > 0 ? `${counts.removed} removed` : null,
    counts.kept > 0 ? `${counts.kept} kept` : null,
    counts.added > 0 ? `${counts.added} added` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(", ");
}

function outcomeLine(org: ImportPreviewOrg): string {
  return countsText(outcomeCounts(org)) || "Nothing is imported";
}

function totalOutcomeCounts(orgs: ImportPreviewOrg[]): OutcomeCounts {
  const total: OutcomeCounts = { replaced: 0, removed: 0, kept: 0, added: 0 };
  for (const counts of orgs.map(outcomeCounts)) {
    total.replaced += counts.replaced;
    total.removed += counts.removed;
    total.kept += counts.kept;
    total.added += counts.added;
  }
  return total;
}

function leftOutNote(orgs: ImportPreviewOrg[]): string | null {
  const staying = orgs.reduce((total, org) => total + sourceItems(org).length, 0);
  if (staying === 0) return null;
  return `${staying} ${plural(staying, "stays", "stay")} behind`;
}

function groupNote(id: ReviewGroupId, orgs: ImportPreviewOrg[]): string | null {
  if (id === "leftOut") return leftOutNote(orgs);
  return countsText(totalOutcomeCounts(orgs)) || null;
}

function replacesLine(org: ImportPreviewOrg): string {
  const replaced = targetItems(org).filter(item => item.outcome === "replaced");
  return `Replaces ${kindPhrases(replaced)}`;
}

function addsLine(org: ImportPreviewOrg): string {
  const added = sourceItems(org).filter(item => item.outcome === "new");
  return added.length > 0 ? `Adds ${kindPhrases(added)}` : "Nothing to add";
}

function lineFor(group: ReviewGroupId, org: ImportPreviewOrg, changes: number): string {
  if (group === "leftOut") return "Not imported";
  if (group === "nothingExisting") return addsLine(org);
  if (changes > 0) return outcomeLine(org);
  if (group === "replaces") return replacesLine(org);
  return outcomeLine(org);
}

function reviewFirm(
  group: ReviewGroupId,
  org: ImportPreviewOrg,
  state: ImportPageState,
): ReviewFirm {
  const key = firmKey(org);
  const control = firmControl(state, key);
  const changes = group === "leftOut" ? 0 : changeCount(org, control);
  return {
    key,
    name: org.companyName ?? "No firm",
    choice: org.clash ? control.choice : null,
    changes,
    line: lineFor(group, org, changes),
  };
}

function byChangedThenName(first: ReviewFirm, second: ReviewFirm): number {
  const changedFirst = first.changes > 0 ? 0 : 1;
  const changedSecond = second.changes > 0 ? 0 : 1;
  if (changedFirst !== changedSecond) return changedFirst - changedSecond;
  return first.name.localeCompare(second.name);
}

export function reviewGroups(preview: ImportPreview, state: ImportPageState): ReviewGroup[] {
  const firmsByGroup: Record<ReviewGroupId, ReviewFirm[]> = {
    replaces: [],
    keeps: [],
    nothingExisting: [],
    leftOut: [],
  };
  const orgsByGroup: Record<ReviewGroupId, ImportPreviewOrg[]> = {
    replaces: [],
    keeps: [],
    nothingExisting: [],
    leftOut: [],
  };
  for (const org of preview.orgs) {
    const group = groupOf(org, state);
    firmsByGroup[group].push(reviewFirm(group, org, state));
    orgsByGroup[group].push(org);
  }
  const hasFirms = (id: ReviewGroupId) => firmsByGroup[id].length > 0;
  const firstTwoEmpty = !hasFirms("replaces") && !hasFirms("keeps");
  return GROUP_ORDER.map(id => ({
    id,
    title: GROUP_TITLES[id],
    note: groupNote(id, orgsByGroup[id]),
    open: hasFirms(id) && (id === "replaces" || (id === "nothingExisting" && firstTwoEmpty)),
    firms: firmsByGroup[id].sort(byChangedThenName),
  }));
}

export function withLeftOutNote(groups: ReviewGroup[], note: string | null): ReviewGroup[] {
  if (!note) return groups;
  return groups.map(group => (group.id === "leftOut" ? { ...group, note } : group));
}

function hiddenChangedText(hidden: ReviewFirm[]): string {
  const changed = hidden.filter(firm => firm.changes > 0).length;
  return changed === 0 ? "none changed" : `${changed} changed`;
}

export function visibleFirms(
  group: ReviewGroup,
  showAll: boolean,
): { firms: ReviewFirm[]; showAllLabel: string | null } {
  if (showAll || group.firms.length <= VISIBLE_FIRM_LIMIT) {
    return { firms: group.firms, showAllLabel: null };
  }
  const hidden = group.firms.slice(VISIBLE_FIRM_LIMIT);
  return {
    firms: group.firms.slice(0, VISIBLE_FIRM_LIMIT),
    showAllLabel: `Show all ${group.firms.length} · ${hidden.length} more, ${hiddenChangedText(hidden)}`,
  };
}

function tickedOrgs(preview: ImportPreview, state: ImportPageState): ImportPreviewOrg[] {
  return preview.orgs.filter(org => isTicked(org, state));
}

function removedExistingItems(preview: ImportPreview, state: ImportPageState): ImportItem[] {
  return tickedOrgs(preview, state).flatMap(org =>
    targetItems(org).filter(item => item.outcome === "removed"),
  );
}

export function removedItemCount(preview: ImportPreview, state: ImportPageState): number {
  return removedExistingItems(preview, state).length;
}

function removedByChangesLabel(removed: ImportItem[]): string {
  const kinds = new Set(removed.map(item => KIND_OF_ITEM[item.kind]));
  const [only] = [...kinds];
  const noun = kinds.size === 1 ? KIND_NOUNS[only][removed.length === 1 ? 0 : 1] : null;
  if (removed.length === 1) return `target ${noun} removed by your change`;
  return `target ${noun ?? "items"} removed by your changes`;
}

function plural(count: number, one: string, many: string): string {
  return count === 1 ? one : many;
}

export function reviewSummary(
  preview: ImportPreview,
  state: ImportPageState,
  mode: ImportMode,
): ReviewFigure[] {
  const ticked = tickedOrgs(preview, state);
  const organisations = previewTotals(preview, state).organisations;
  const replacedOrRemoved = ticked
    .flatMap(targetItems)
    .filter(item => item.outcome === "replaced" || item.outcome === "removed").length;
  const figures: ReviewFigure[] = [
    {
      value: organisations,
      label: plural(organisations, "organisation coming in", "organisations coming in"),
    },
    {
      value: replacedOrRemoved,
      label: plural(
        replacedOrRemoved,
        "target item replaced or removed",
        "target items replaced or removed",
      ),
    },
  ];
  const removed = removedExistingItems(preview, state);
  if (removed.length > 0) {
    figures.push({ value: removed.length, label: removedByChangesLabel(removed), warn: true });
  }
  if (mode === "move") {
    const leaving = ticked.flatMap(sourceItems).filter(item => item.outcome === "new").length;
    figures.push({
      value: leaving,
      label: `${plural(leaving, "source item", "source items")} moved out`,
      warn: true,
    });
  }
  return figures;
}

export function replaceWarning(preview: ImportPreview, state: ImportPageState): string | null {
  const replacing = reviewGroups(preview, state).find(group => group.id === "replaces");
  if (!replacing || replacing.firms.length === 0) return null;
  const base = `Replaces target content for ${replacing.firms.length}`;
  const removed = removedExistingItems(preview, state);
  if (removed.length === 0) return base;
  return `${base} · ${removed.length} ${removedByChangesLabel(removed)}`;
}

export function nothingToCopy(preview: ImportPreview, state: ImportPageState): boolean {
  return previewTotals(preview, state).organisations === 0;
}

export function reviewEmptyState(
  kinds: string[],
  preview: ImportPreview | null,
  state: ImportPageState,
): "no_kinds" | "nothing_to_copy" | null {
  if (kinds.length === 0) return "no_kinds";
  if (preview && nothingToCopy(preview, state)) return "nothing_to_copy";
  return null;
}

export function importButtonLabel(args: {
  mode: ImportMode;
  sarsMismatch: boolean;
  nothingToCopy: boolean;
  removing?: number;
  purpose?: string | null;
}): string {
  if (args.nothingToCopy) {
    const removing = args.removing ?? 0;
    if (removing > 0) return `Remove ${removing} ${plural(removing, "item", "items")}`;
    if (args.purpose) return "Mark as done";
    return args.mode === "move" ? "Move content" : "Import content";
  }
  if (args.sarsMismatch) return "Remove scores and import";
  return args.mode === "move" ? "Move content" : "Import";
}

export function isEmptyImportIdle(args: {
  nothingToCopy: boolean;
  removing: number;
  purpose: string | null;
}): boolean {
  return args.nothingToCopy && args.removing === 0 && args.purpose === null;
}

export function emptyImportNote(mode: ImportMode): string {
  return mode === "move" ? "There is nothing to move." : "There is nothing to import.";
}

function dayAndMonth(iso: string | null): string {
  if (!iso) return "";
  const text = new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  return ` (${text})`;
}

function languageNameOf(item: ImportItem): string {
  return item.languageIso ? `${languageName(item.languageIso)} ` : "";
}

function rankingNoun(item: ImportItem): string {
  return item.tier === null ? "ranking" : `Tier ${item.tier}`;
}

function describe(item: ImportItem, side: "source" | "target"): string {
  const lead = item.contactName ? `${item.contactName}'s ${side}` : `The ${side}'s`;
  if (item.kind === "submission") {
    return `${lead} ${languageNameOf(item)}submission${dayAndMonth(item.submittedAt)}`;
  }
  if (item.kind === "commentary") return `${lead} commentary`;
  const table = item.tableName ?? item.landingTableName;
  return `${lead} ${rankingNoun(item)}${table ? ` in ${table}` : ""}`;
}

function sharedSlotSentence(incoming: ImportItem, existing: ImportItem): string {
  if (incoming.outcome !== "new") {
    return `${describe(existing, "target")} stays. The source's one${dayAndMonth(incoming.submittedAt)} is left out.`;
  }
  if (incoming.kind === "firm_ranking" || incoming.kind === "lawyer_ranking") {
    const table = existing.tableName ?? incoming.landingTableName ?? incoming.tableName;
    const lead = incoming.contactName ? `${incoming.contactName}'s source` : "The source's";
    return `${lead} ${rankingNoun(incoming)} replaces the target's ${rankingNoun(existing)}${table ? ` in ${table}` : ""}.`;
  }
  return `${describe(incoming, "source")} replaces the target's one${dayAndMonth(existing.submittedAt)}.`;
}

function countOf(items: ImportItem[], owner: "source" | "target"): string {
  return items.length === 1 ? `the ${owner}'s one` : `the ${owner}'s ${items.length}`;
}

function unpairedSourceSentence(
  item: ImportItem,
  existing: ImportItem[],
  firmHasExisting: boolean,
): string {
  if (item.outcome !== "new") return `${describe(item, "source")} is left out.`;
  if (existing.length === 0) {
    return firmHasExisting
      ? `${describe(item, "source")} is added. The target had none.`
      : `${describe(item, "source")} is added.`;
  }
  if (existing.every(member => member.outcome === "replaced")) {
    return `${describe(item, "source")} is added, replacing ${countOf(existing, "target")}.`;
  }
  return `${describe(item, "source")} is added.`;
}

function unpairedTargetSentence(item: ImportItem, incoming: ImportItem[]): string {
  const bringing = incoming.filter(member => member.outcome === "new");
  if (item.outcome === "removed") {
    return incoming.length === 0
      ? `${describe(item, "target")} is removed. Nothing from the source replaces it.`
      : `${describe(item, "target")} is removed.`;
  }
  if (item.outcome === "replaced") {
    return bringing.length === 0
      ? `${describe(item, "target")} is replaced.`
      : `${describe(item, "target")} is replaced by ${countOf(bringing, "source")}.`;
  }
  return `${describe(item, "target")} stays.`;
}

function asLeftOut(org: ImportPreviewOrg): ImportPreviewOrg {
  const staysOut = (item: ImportItem): ImportItem => ({ ...item, outcome: "not_brought_across" });
  const stays = (item: ImportItem): ImportItem => ({ ...item, outcome: "kept" });
  return {
    ...org,
    items: { source: sourceItems(org).map(staysOut), target: targetItems(org).map(stays) },
  };
}

export function itemLines(firm: ImportPreviewOrg, options: { leftOut?: boolean } = {}): ItemLine[] {
  const org = options.leftOut ? asLeftOut(firm) : firm;
  const firmHasExisting = targetItems(org).length > 0;
  const entries = [
    ...sourceItems(org).map(item => ({ item, side: "source" as const })),
    ...targetItems(org).map(item => ({ item, side: "target" as const })),
  ];
  return slotsInOrder(entries, entry => entry.item).flatMap(members => {
    const incoming = members.filter(member => member.side === "source");
    const existing = members.filter(member => member.side === "target");
    const paired = incoming.length === 1 && existing.length === 1 && incoming[0].item.sharedSlot;
    if (paired) {
      return [
        {
          kindLabel: KIND_LABEL_OF_ITEM[incoming[0].item.kind],
          sentence: sharedSlotSentence(incoming[0].item, existing[0].item),
          itemIds: members.map(member => member.item.id),
          removedWithNothing: false,
        },
      ];
    }
    return members.map(member => ({
      kindLabel: KIND_LABEL_OF_ITEM[member.item.kind],
      sentence:
        member.side === "source"
          ? unpairedSourceSentence(
              member.item,
              existing.map(other => other.item),
              firmHasExisting,
            )
          : unpairedTargetSentence(
              member.item,
              incoming.map(other => other.item),
            ),
      itemIds: [member.item.id],
      removedWithNothing:
        member.side === "target" && member.item.outcome === "removed" && incoming.length === 0,
    }));
  });
}
